/**
 * Mobile-specific utilities for preventing screen sleep
 * Uses multiple non-intrusive strategies to ensure the screen stays awake
 */

// Debug flag - set to true to enable mobile wake lock logging
const DEBUG_MOBILE_WAKELOCK = false;

export class MobileWakeLock {
  private strategies: (() => void)[] = [];
  private cleanupFunctions: (() => void)[] = [];
  private isActive = false;
  private intervalId: NodeJS.Timeout | null = null;
  private audioElement: HTMLAudioElement | null = null;

  constructor() {
    this.setupStrategies();
  }

  private setupStrategies() {
    // Strategy 1: Play silent audio
    this.strategies.push(() => {
      try {
        if (!this.audioElement) {
          this.audioElement = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZijYIG2m98OScTgwOUqzn4LFhGgU7k9n1148wBSSAzO/bkEIKFl+05+OlUhEIS6Pg7bllHgg+l9n1y3kqCCl+yenajzwMHWy/8OOeTQ0TV6rq7LJiGAY2k9f0030uBCmDy+vckTwLHWq+5+OdTg0TV6rq7rJhGAU7k9n1yn0uBCyBzvDYiTYJGWu98eSeTQ0RU6zm77NlGwU3k9f1yHkqBSuAzO7ZiDUKHWq+5+OdTgsOUqvm7rJhGAU2lNj1yHkpBSuAzO7ZiDYJGWu98eSdTQ0RU6zm77FlGwU3k9f1yHkqBSuAz+7YiDQKHWu+5+KcTgsOUqvm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGwU0kNf2yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj1yHkqBSyAzu7YiDQKHWu+5+OcTQsNUqzm7rJhGAU2lNj12IQnBiiC0+/Wi');
          this.audioElement.loop = true;
          this.audioElement.volume = 0.01;
          
          // Store cleanup function
          this.cleanupFunctions.push(() => {
            if (this.audioElement) {
              this.audioElement.pause();
              this.audioElement.remove();
              this.audioElement = null;
            }
          });
        }

        if (this.audioElement && this.audioElement.paused) {
          this.audioElement.play().catch(() => {
            if (DEBUG_MOBILE_WAKELOCK) console.log('[MobileWakeLock] Silent audio play failed');
          });
        }
      } catch (e) {
        if (DEBUG_MOBILE_WAKELOCK) console.log('[MobileWakeLock] Audio strategy failed:', e);
      }
    });

    // Strategy 2: Update page title
    this.strategies.push(() => {
      const originalTitle = document.title;
      document.title = originalTitle + ' ';
      setTimeout(() => {
        document.title = originalTitle;
      }, 100);
    });

    // Strategy 3: Trigger a minimal DOM update
    this.strategies.push(() => {
      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.top = '-9999px';
      el.style.width = '1px';
      el.style.height = '1px';
      el.setAttribute('aria-hidden', 'true');
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 100);
    });

    // Strategy 4: Use requestAnimationFrame
    this.strategies.push(() => {
      let count = 0;
      const animate = () => {
        count++;
        if (count < 10 && this.isActive) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    });
  }

  start() {
    if (this.isActive) {
      if (DEBUG_MOBILE_WAKELOCK) console.log('[MobileWakeLock] Already active, not starting again');
      return;
    }

    if (DEBUG_MOBILE_WAKELOCK) console.log('[MobileWakeLock] Starting mobile wake lock strategies');
    this.isActive = true;

    // Execute all strategies immediately
    this.strategies.forEach(strategy => {
      try {
        strategy();
      } catch (e) {
        if (DEBUG_MOBILE_WAKELOCK) console.error('[MobileWakeLock] Strategy failed:', e);
      }
    });

    // Then execute periodically
    this.intervalId = setInterval(() => {
      if (this.isActive) {
        this.strategies.forEach(strategy => {
          try {
            strategy();
          } catch (e) {
            if (DEBUG_MOBILE_WAKELOCK) console.error('[MobileWakeLock] Strategy failed:', e);
          }
        });
      }
    }, 15000); // Every 15 seconds
  }

  stop() {
    if (DEBUG_MOBILE_WAKELOCK) console.log('[MobileWakeLock] Stopping mobile wake lock strategies');
    this.isActive = false;

    // Clear interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Execute all cleanup functions
    this.cleanupFunctions.forEach(cleanup => {
      try {
        cleanup();
      } catch (e) {
        if (DEBUG_MOBILE_WAKELOCK) console.error('[MobileWakeLock] Cleanup failed:', e);
      }
    });
    
    // Clear cleanup functions
    this.cleanupFunctions = [];
    
    // Stop audio if it exists
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.remove();
      this.audioElement = null;
    }
  }
}

// Singleton instance
export const mobileWakeLock = new MobileWakeLock();
